import EmptyView from "common/Empty/EmptyView";
import React, { useContext } from "react";
import { AffiliationTabs } from "features/affiliation/common/types";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTabToDisplay } from "features/affiliation/common/hooks/misc.ts";
import { LanguageNameSpaces } from "@shared/types";
import { useTheme } from "styled-components";
import { Copy, Plus } from "lucide-react";
import Button from "common/Buttons/Button";
import PanelEmpty from "common/Empty/PanelEmpty";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";

interface ReferralEmptyStateProps {
  tab:
    | AffiliationTabs.REFERRAL_LINKS
    | AffiliationTabs.REFERRED_USERS
    | AffiliationTabs.EXISTING_USERS;
  isSearch?: boolean;
}

const ReferralEmptyState = ({ tab, isSearch }: ReferralEmptyStateProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const tabToDisplay = useTabToDisplay({ fullName: true });

  const {
    createOrCopyReferralLink,
    copyPromoCode,
    getPromoCodeLoading,
    getReferralLinkLoading,
    isAdmin,
    hasFeatureFlag,
  } = useContext(AffiliationContext)!;
  const isExistingUsersTab = tab === AffiliationTabs.EXISTING_USERS;

  const getButtonText = () => {
    if (
      tab === AffiliationTabs.REFERRAL_LINKS ||
      tab === AffiliationTabs.REFERRED_USERS
    ) {
      return hasFeatureFlag
        ? t("empty.new_referral_link")
        : t("empty.your_referral_link");
    } else if (isAdmin) {
      return t("empty.your_promo_code");
    }
  };

  const getButtonIcon = () => {
    if (
      (tab === AffiliationTabs.REFERRAL_LINKS ||
        tab === AffiliationTabs.REFERRED_USERS) &&
      hasFeatureFlag
    )
      return <PlusOutlined />;
    else return <CopyOutlined />;
  };

  // New look: inside the table's panel, a short title, one line and one
  // quiet action (the page's toolbar holds the primary one).
  if (isNewLook) {
    if (isSearch)
      return (
        <PanelEmpty
          title={tCommon("empty.no_results_matching_search", {
            objectName: tabToDisplay[tab].toLowerCase(),
          })}
        />
      );
    const buttonText = getButtonText();
    return (
      <PanelEmpty
        title={t("empty.affiliation_empty_title", {
          objectName: tabToDisplay[tab],
        })}
        description={t(TAB_TO_EMPTY_DESCRIPTION[tab])}
        action={
          buttonText && (
            <Button
              onClick={
                isExistingUsersTab ? copyPromoCode : createOrCopyReferralLink
              }
              loading={
                isExistingUsersTab
                  ? getPromoCodeLoading
                  : getReferralLinkLoading
              }
            >
              {isExistingUsersTab || !hasFeatureFlag ? (
                <Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              ) : (
                <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              )}
              {buttonText}
            </Button>
          )
        }
        data-testid="affiliation-empty"
      />
    );
  }

  return (
    <>
      <EmptyView
        iconKey={
          tab === AffiliationTabs.REFERRAL_LINKS
            ? EmptyStatesType.Empty
            : EmptyStatesType.Users
        }
        iconWidth={isMobile ? 100 : 120}
        title={t("empty.affiliation_empty_title", {
          objectName: tabToDisplay[tab],
        })}
        buttonText={getButtonText()}
        buttonIcon={getButtonIcon()}
        buttonOnClick={
          isExistingUsersTab ? copyPromoCode : createOrCopyReferralLink
        }
        buttonLoading={
          isExistingUsersTab ? getPromoCodeLoading : getReferralLinkLoading
        }
        isSearch={isSearch}
      />
    </>
  );
};

export default ReferralEmptyState;

const TAB_TO_EMPTY_DESCRIPTION = {
  [AffiliationTabs.REFERRAL_LINKS]: "links.empty_description",
  [AffiliationTabs.REFERRED_USERS]: "users.referred_empty_description",
  [AffiliationTabs.EXISTING_USERS]: "users.existing_empty_description",
} as const;
